import React, { useState, useEffect, useRef } from 'react';

const GreekTimeInput = ({ value, onChange, placeholder = "00:00" }) => {
  const [hours, setHours] = useState('00');
  const [minutes, setMinutes] = useState('00');
  const [isHoursOpen, setIsHoursOpen] = useState(false);
  const [isMinutesOpen, setIsMinutesOpen] = useState(false);
  const hoursRef = useRef(null);
  const minutesRef = useRef(null);

  useEffect(() => {
    if (value) {
      const [h, m] = value.split(':');
      setHours(h || '00');
      setMinutes(m || '00');
    }
  }, [value]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (hoursRef.current && !hoursRef.current.contains(event.target)) {
        setIsHoursOpen(false);
      }
      if (minutesRef.current && !minutesRef.current.contains(event.target)) {
        setIsMinutesOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Generate hour options starting from 08:00
  // 08-23 (16 items) followed by 00-07 (8 items)
  const hourOptions = [
    ...Array.from({ length: 16 }, (_, i) => String(i + 8).padStart(2, '0')), // 08-23
    ...Array.from({ length: 8 }, (_, i) => String(i).padStart(2, '0'))        // 00-07
  ];

  // Generate minute options
  const minuteOptions = ['00', '15', '30', '45'];

  const handleHourChange = (newHour) => {
    setHours(newHour);
    onChange(`${newHour}:${minutes}`);
    setIsHoursOpen(false);
  };

  const handleMinuteChange = (newMinute) => {
    setMinutes(newMinute);
    onChange(`${hours}:${newMinute}`);
    setIsMinutesOpen(false);
  };

  return (
    <div className="flex items-center space-x-1">
      {/* Hours Dropdown */}
      <div className="relative" ref={hoursRef}>
        <button
          type="button"
          onClick={() => setIsHoursOpen(!isHoursOpen)}
          className="w-14 sm:w-16 px-1 sm:px-2 py-2 bg-[#111827] border border-gray-600 rounded-md text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center font-mono text-xs sm:text-sm hover:bg-gray-800 transition-colors flex items-center justify-between"
        >
          <span>{hours}</span>
          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        
        {isHoursOpen && (
          <div className="absolute top-full left-0 mt-1 bg-[#1F2937] border border-gray-600 rounded-lg shadow-xl z-50 w-14 sm:w-16 max-h-48 overflow-y-auto custom-scrollbar">
            {hourOptions.map((hour) => (
              <button
                key={hour}
                onClick={() => handleHourChange(hour)}
                className={`w-full px-2 py-2 text-center font-mono text-xs sm:text-sm hover:bg-gray-600 transition-colors ${
                  hour === hours ? 'bg-blue-600 text-white' : 'text-gray-300'
                }`}
              >
                {hour}
              </button>
            ))}
          </div>
        )}
      </div>
      
      <span className="text-gray-400 text-xs sm:text-sm font-bold">:</span>
      
      {/* Minutes Dropdown */}
      <div className="relative" ref={minutesRef}>
        <button
          type="button"
          onClick={() => setIsMinutesOpen(!isMinutesOpen)}
          className="w-14 sm:w-16 px-1 sm:px-2 py-2 bg-[#111827] border border-gray-600 rounded-md text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center font-mono text-xs sm:text-sm hover:bg-gray-800 transition-colors flex items-center justify-between"
        >
          <span>{minutes}</span>
          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        
        {isMinutesOpen && (
          <div className="absolute top-full left-0 mt-1 bg-[#1F2937] border border-gray-600 rounded-lg shadow-xl z-50 w-14 sm:w-16 max-h-40 overflow-y-auto custom-scrollbar">
            {minuteOptions.map((minute) => (
              <button
                key={minute}
                onClick={() => handleMinuteChange(minute)}
                className={`w-full px-2 py-2 text-center font-mono text-xs sm:text-sm hover:bg-gray-600 transition-colors ${
                  minute === minutes ? 'bg-blue-600 text-white' : 'text-gray-300'
                }`}
              >
                {minute}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default GreekTimeInput;